// src/Views/Hero.js — Flasher hero with rotating word, gradient italic, parallax.
import React from 'react';
import { Eyebrow, RotatingWord, ParallaxLayer, MagneticButton, GhostButton } from '../components/primitives';
import { ROTATE_WORDS, STATS } from './siteData';

function OrbitalRings() {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 1200 800"
      preserveAspectRatio="xMidYMid slice"
      style={{ opacity: 0.4 }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="hero-ring-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFB13F" stopOpacity="0.7" />
          <stop offset="1" stopColor="#00DFBB" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <g style={{ transformOrigin: '600px 400px', animation: 'spinSlow 80s linear infinite' }}>
        <ellipse cx="600" cy="400" rx="520" ry="220" fill="none" stroke="url(#hero-ring-grad)" strokeWidth="1" strokeDasharray="2 6" />
      </g>
      <g style={{ transformOrigin: '600px 400px', animation: 'spinSlowR 110s linear infinite' }}>
        <ellipse cx="600" cy="400" rx="380" ry="160" fill="none" stroke="var(--purple)" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="1 4" />
      </g>
      <g style={{ transformOrigin: '600px 400px', animation: 'spinSlow 140s linear infinite' }}>
        <ellipse cx="600" cy="400" rx="260" ry="100" fill="none" stroke="var(--cyan)" strokeOpacity="0.3" strokeWidth="1" strokeDasharray="1 3" />
      </g>
      <style>{`
        @keyframes spinSlow { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        @keyframes spinSlowR { from { transform: rotate(0); } to { transform: rotate(-360deg); } }
        body[data-motion="reduced"] svg g { animation: none !important; }
      `}</style>
    </svg>
  );
}

function SystemSchematic() {
  return (
    <div
      style={{
        fontFamily: 'var(--f-mono)',
        fontSize: 10,
        color: 'var(--ink-3)',
        lineHeight: 1.7,
        border: '1px solid var(--line-strong)',
        padding: '12px 14px',
        borderRadius: 12,
        background: 'rgba(10,6,24,0.5)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
      }}
    >
      <div style={{ color: 'var(--magenta)', marginBottom: 6, letterSpacing: '0.16em' }}>{'// METHOD.LOG'}</div>
      <div><span style={{ color: 'var(--cyan)' }}>think</span> ─▶ decide</div>
      <div><span style={{ color: 'var(--cyan)' }}>decide</span> ─▶ direct</div>
      <div><span style={{ color: 'var(--cyan)' }}>direct</span> ─▶ <span style={{ color: 'var(--magenta)' }}>agents.build()</span></div>
      <div style={{ marginTop: 6, color: 'var(--ink-2)' }}>self.code = <span style={{ color: 'var(--magenta)' }}>0 lines</span></div>
    </div>
  );
}

const Hero = () => {
  const showSchematic = typeof window !== 'undefined' ? window.innerWidth >= 1100 : true;
  return (
    <section
      id="top"
      style={{
        position: 'relative',
        minHeight: '100vh',
        padding: '180px 28px 160px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <ParallaxLayer depth={0.02} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <OrbitalRings />
      </ParallaxLayer>

      <div style={{ position: 'relative', maxWidth: 1280, width: '100%', zIndex: 2 }}>
        <ParallaxLayer depth={-0.015}>
          <Eyebrow>SOLOMON.FOSHKO ⏵ INDEPENDENT BUILDER ⏵ EST. 2024</Eyebrow>
        </ParallaxLayer>

        <ParallaxLayer depth={-0.025}>
          <h1
            style={{
              fontFamily: 'var(--f-display)',
              fontSize: 'clamp(56px, 11vw, 168px)',
              fontWeight: 700,
              lineHeight: 1.02,
              letterSpacing: '-0.04em',
              margin: 0,
              color: 'var(--ink)',
              paddingBottom: '0.15em',
            }}
          >
            <span style={{ display: 'block' }}>
              I build{' '}
              <em
                style={{
                  fontStyle: 'italic',
                  fontWeight: 400,
                  display: 'inline-block',
                  paddingRight: '0.18em',
                  marginRight: '-0.05em',
                  background: 'linear-gradient(120deg, var(--magenta) 0%, var(--purple) 50%, var(--cyan) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                ideas
              </em>
            </span>
            <span style={{ display: 'block', textAlign: 'right' }}>
              into working <RotatingWord words={ROTATE_WORDS} />
            </span>
          </h1>
        </ParallaxLayer>

        <ParallaxLayer depth={-0.01}>
          <div
            className="hero-bottom-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 360px)',
              gap: 48,
              alignItems: 'end',
              marginTop: 56,
              flexWrap: 'wrap',
            }}
          >
            <p style={{
              fontFamily: 'var(--f-body)',
              fontSize: 'clamp(16px, 1.4vw, 20px)',
              lineHeight: 1.55,
              color: 'var(--ink-2)',
              maxWidth: 580,
              margin: 0,
            }}>
              Board games with mechanical physics. Civic safety copilots.
              Archery coaching platforms. Games industry intelligence.
              <br />
              <span style={{ color: 'var(--ink)' }}>Same builder. Same method. Different worlds.</span>{' '}
              AI agents are my construction medium → I think, decide, and direct.
            </p>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
              <MagneticButton href="#builds" size="lg" accent="magenta">
                See the builds <span>→</span>
              </MagneticButton>
              <GhostButton href="#contact" size="lg">
                Work with me
              </GhostButton>
            </div>
          </div>
        </ParallaxLayer>

        {showSchematic && (
          <div
            style={{
              position: 'absolute',
              top: -20,
              right: -10,
              width: 220,
              pointerEvents: 'none',
            }}
          >
            <ParallaxLayer depth={0.04}>
              <SystemSchematic />
            </ParallaxLayer>
          </div>
        )}
      </div>

      {/* Stats strip */}
      <div
        style={{
          position: 'absolute',
          bottom: 32,
          left: 28,
          right: 28,
          zIndex: 2,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 24,
          flexWrap: 'wrap',
          fontFamily: 'var(--f-mono)',
        }}
      >
        {STATS.map((s) => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: 'var(--magenta)',
                textShadow: '0 0 20px rgba(255,177,63,0.4)',
              }}
            >
              {s.num}
            </span>
            <span
              style={{
                fontSize: 11,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'var(--ink-3)',
              }}
            >
              {s.label}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Hero;
